/**
 * Conversa com a API.
 *
 * Toda chamada leva o token do Firebase. O `getIdToken()` renova sozinho quando
 * está perto de expirar, então não existe aqui nenhum controle de validade —
 * era exatamente isso que a aba "Sessões" fazia à mão no sistema antigo.
 */

import { auth } from './firebase.js';

const BASE = import.meta.env['VITE_API'] ?? 'http://localhost:3000';

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhes?: string[],
    /**
     * O que fazer em vez disso, quando a API sabe.
     *
     * Existe porque algumas recusas são conselhos disfarçados: item com
     * histórico não pode ser apagado, mas pode ser desativado — e a API já diz
     * isso. Sem carregar o campo até aqui, a tela mostraria só "não pode" e a
     * pessoa concluiria que o sistema travou.
     */
    readonly sugestao?: string,
  ) {
    super(mensagem);
  }
}

async function chamar<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  const usuario = auth.currentUser;

  // Sem sessão, a chamada VAI assim mesmo, sem o cabeçalho de autorização.
  //
  // A versão anterior recusava aqui, na tela. Parecia mais eficiente, mas era
  // errado: colocava a tela decidindo quem tem acesso, quando quem decide é o
  // servidor. Duas cópias da mesma regra divergem — e a da tela é a que não
  // vale, porque qualquer um pode contorná-la. Aqui a resposta 401 vem do
  // backend, com a mensagem dele.
  const autorizacao = usuario ? { Authorization: `Bearer ${await usuario.getIdToken()}` } : {};

  const resposta = await fetch(BASE + caminho, {
    ...opcoes,
    // Necessário para o navegador GUARDAR o cookie de sessão que `/api/sessao`
    // devolve: numa chamada entre origens diferentes (a tela e a API), sem isto
    // o `Set-Cookie` é descartado em silêncio. O cookie só vale em `/api/fotos`,
    // então nas outras chamadas isto não manda nada a mais.
    credentials: 'include',
    headers: {
      /**
       * Só declara JSON quando há corpo JSON de verdade.
       *
       * Declarar `application/json` numa requisição SEM corpo faz o Fastify
       * recusar com 400 antes de a rota rodar — e a recusa vem no formato de
       * erro dele (`message`), não no nosso (`erro`), então a tela mostrava
       * "Algo deu errado" para um problema que não era do servidor nem do
       * pedido. Isso valia para TODO `DELETE`, incluindo o de sair, que assim
       * deixava o cookie das fotos no navegador.
       */
      ...(opcoes.body && !(opcoes.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...autorizacao,
      ...opcoes.headers,
    },
  });

  if (!resposta.ok) {
    // A API sempre responde JSON com `erro`; se vier outra coisa, é falha de
    // infraestrutura e a mensagem crua não ajudaria ninguém.
    let corpo: { erro?: string; detalhes?: string[]; sugestao?: string } = {};
    try {
      corpo = await resposta.json();
    } catch {
      throw new ErroApi(resposta.status, 'O servidor não respondeu como esperado.');
    }
    throw new ErroApi(resposta.status, corpo.erro ?? 'Algo deu errado.', corpo.detalhes, corpo.sugestao);
  }
  return resposta.json() as Promise<T>;
}

export const api = {
  get: <T>(caminho: string) => chamar<T>(caminho),
  post: <T>(caminho: string, corpo: unknown) =>
    chamar<T>(caminho, { method: 'POST', body: JSON.stringify(corpo) }),
  put: <T>(caminho: string, corpo: unknown) =>
    chamar<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) }),
  patch: <T>(caminho: string, corpo: unknown) =>
    chamar<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) }),
  del: <T>(caminho: string) => chamar<T>(caminho, { method: 'DELETE' }),
  /** Multipart genérico — para rotas que recebem arquivo e devolvem JSON. */
  enviarMultipart: <T>(caminho: string, corpo: FormData) =>
    chamar<T>(caminho, { method: 'POST', body: corpo }),
  /** Sobe uma foto e devolve onde ela ficou. O corpo é multipart, não JSON. */
  enviarFoto: async (
    pasta: 'itens' | 'notas' | 'entregas' | 'retiradas',
    arquivo: File,
    referencia?: string,
  ): Promise<{ caminho: string; url: string }> => {
    const corpo = new FormData();
    corpo.append('pasta', pasta);
    if (referencia) corpo.append('referencia', referencia);
    corpo.append('arquivo', arquivo);
    return chamar('/api/fotos', { method: 'POST', body: corpo });
  },
  /** O endereço de uma foto, já com o prefixo da API. */
  foto: (caminho: string | null) =>
    !caminho ? null : caminho.startsWith('http') ? caminho : BASE + caminho,
  fotoAutenticada,
};

/**
 * Baixa uma foto PELO MESMO CANAL do resto da API — com o cabeçalho de
 * autorização — e devolve um endereço local (blob) pronto para `<img src>`.
 *
 * Existe porque o caminho normal das fotos (`<img src>` + cookie) tem um elo
 * que o resto da API não tem: o cookie. Quando ele falta, expira ou é barrado
 * no meio do caminho, a foto morre em silêncio — foi o "as fotos vivem caindo"
 * de 04/08. Este canal não depende de cookie nenhum: usa o token do Firebase,
 * que se renova sozinho. Se a API responde, a foto vem.
 *
 * Usos: a foto grande do clique (`foto-grande.ts`) e o socorro de `<img>` que
 * falhou (`comum/foto.ts`). O caminho normal continua sendo o cookie, que
 * aproveita o cache do navegador.
 */
const fotosBaixadas = new Map<string, Promise<string>>();

function fotoAutenticada(url: string): Promise<string> {
  // Endereço de fora (busca de imagens, links antigos) não é nosso para
  // autenticar — vai como está. "Nossa" é a que mora na origem da API — que em
  // produção é a própria origem da página (BASE vazia, Hosting encaminha) — e
  // sob /api/. Comparar por origem, e não por prefixo de texto, porque a
  // MESMA foto chega aqui das duas formas: relativa ("/api/fotos/...") vinda
  // do JSON, e absoluta vinda de `img.src`, que o navegador sempre resolve.
  if (url.startsWith('data:') || url.startsWith('blob:')) return Promise.resolve(url);
  try {
    const alvo = new URL(url, location.href);
    const nossa = new URL(BASE || '/', location.href);
    if (alvo.origin !== nossa.origin || !alvo.pathname.startsWith('/api/')) {
      return Promise.resolve(url);
    }
  } catch {
    return Promise.resolve(url);
  }

  const guardada = fotosBaixadas.get(url);
  if (guardada) return guardada;

  const baixando = (async () => {
    const usuario = auth.currentUser;
    const resposta = await fetch(url, {
      headers: usuario ? { Authorization: `Bearer ${await usuario.getIdToken()}` } : {},
    });
    if (!resposta.ok) throw new ErroApi(resposta.status, 'A foto não veio.');
    return URL.createObjectURL(await resposta.blob());
  })();

  // Sucesso fica guardado para sempre (a sessão inteira); falha sai do mapa,
  // senão um tropeço de rede condenaria a foto até recarregar a página.
  fotosBaixadas.set(url, baixando);
  baixando.catch(() => fotosBaixadas.delete(url));
  return baixando;
}

// ---- formatos que a API devolve, espelhando o backend ----

export interface ItemDaLista {
  id: number;
  codigo: string;
  nome: string;
  unidade: string | null;
  saldo: number;
  categoria: string | null;
  grupo: string | null;
  subgrupo: string | null;
  medida: string;
  medida2: string;
  foto: string | null;
  /** Data URI embutido — aparece junto com o JSON, sem requisição extra. */
  miniatura: string | null;
}

export interface BuscaDeItens {
  total: number;
  pagina: number;
  paginas: number;
  porSemelhanca: boolean;
  itens: ItemDaLista[];
}

export interface Faceta {
  id: number;
  nome: string;
  itens: number;
}
